import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ViewIds } from '@ardoq/api-types';
import { t } from '@ardoq/i18n';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import {
  RadialChart,
  type RadialChartProps,
  type SpiderViewSettings,
} from '@ardoq/xyz-charts';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { ChartEmptyState } from 'tabview/chartShared/ChartEmptyState';
import { useRadialChartHost } from '../useRadialChartHost';
import type { SpiderSourceData } from './sourceData/buildSpiderSourceData';
import type { SpiderViewProperties } from './types';

const SpiderLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ChartArea = styled(VisualizationContainer)`
  overflow: hidden;
  min-height: 0;
`;

const NO_CAPTIONS: string[] = [];

export type SpiderViewProps = SpiderSourceData & {
  viewSettings: SpiderViewSettings;
};

export const SpiderView = ({
  plot,
  points,
  viewSettings,
  isViewEmpty,
  emptyReason,
  excludedCount,
  numericFields,
  viewInstanceId,
  locale,
  i18nLocale,
}: SpiderViewProps) => {
  const {
    setContainer,
    container,
    chartColors,
    createExportCanvas,
    onExportCanvasAvailable,
    format,
  } = useRadialChartHost(locale);

  useEffect(() => {
    registerViewPane({
      viewId: ViewIds.SPIDER_2,
      viewProperties: {
        viewSettings,
        numericFields,
        i18nLocale,
        isEmptyView: isViewEmpty,
        viewInstanceId,
      } satisfies SpiderViewProperties,
      isLegendActive: false,
      getContainer: () => container,
      createExportCanvas,
      viewInstanceId,
    });
  }, [
    viewSettings,
    numericFields,
    i18nLocale,
    isViewEmpty,
    viewInstanceId,
    container,
    createExportCanvas,
  ]);

  const formatOtherSeries = useCallback(
    (count: number) =>
      t(
        i18nLocale,
        'views:radialCharts.otherSeries',
        { count },
        '{{count}} more'
      ),
    [i18nLocale]
  );

  const formatAccessibleName = useMemo<
    RadialChartProps['formatAccessibleName']
  >(
    () =>
      ({ seriesCount }) =>
        t(
          i18nLocale,
          seriesCount === 1
            ? 'views:spider2.accessibleName_one'
            : 'views:spider2.accessibleName_other',
          { count: seriesCount },
          seriesCount === 1
            ? 'Spider chart with 1 shape. Use the arrow keys to read the values.'
            : 'Spider chart with {{count}} shapes. Use the arrow keys to read the values.'
        ),
    [i18nLocale]
  );

  const exclusionNotice = t(
    i18nLocale,
    'views:spider2.exclusionNotice',
    { plotted: points.length, total: points.length + excludedCount },
    '{{plotted}} of {{total}} components plotted. Components without a value for any chosen field are not shown.'
  );
  const hasExclusions = excludedCount > 0 && !isViewEmpty;
  const exportCaptions = useMemo(
    () => (hasExclusions ? [exclusionNotice] : []),
    [hasExclusions, exclusionNotice]
  );

  const header = (
    <FlexBox
      padding={hasExclusions ? 'small' : 'none'}
      paddingBottom="none"
      role="status"
      aria-live="polite"
    >
      {hasExclusions && (
        <Text color="textSubtle" variant="caption">
          {exclusionNotice}
        </Text>
      )}
    </FlexBox>
  );

  if (isViewEmpty) {
    return (
      <SpiderLayout ref={setContainer}>
        {header}
        <ChartEmptyState
          emptyReason={emptyReason}
          noFieldsSelected={{
            title: t(
              i18nLocale,
              'views:spider2.emptyState.noFieldsSelected.title',
              'Choose the fields to compare'
            ),
            message: t(
              i18nLocale,
              'views:spider2.emptyState.noFieldsSelected.message',
              'Pick one or more numeric fields in the settings bar above. Each field becomes a shape, with a spoke for every component that has a value.'
            ),
          }}
          noComponents={{
            title: t(
              i18nLocale,
              'views:spider2.emptyState.noComponents.title',
              'There is nothing in view to compare'
            ),
            message: t(
              i18nLocale,
              'views:spider2.emptyState.noComponents.message',
              'No components are in view, so there are no shapes to draw.'
            ),
          }}
          tooFewPositions={{
            title: t(
              i18nLocale,
              'views:spider2.emptyState.tooFewSpokes.title',
              'Not enough spokes to draw a shape'
            ),
            explanation: t(
              i18nLocale,
              'views:spider2.emptyState.tooFewSpokes.message1',
              'A spider chart needs at least three spokes to draw a shape.'
            ),
            remedy: t(
              i18nLocale,
              'views:spider2.emptyState.tooFewSpokes.message2',
              'Each component is a spoke, so include three or more components that have values for the chosen fields.'
            ),
          }}
        />
      </SpiderLayout>
    );
  }

  return (
    <SpiderLayout ref={setContainer}>
      {header}
      <ChartArea>
        <RadialChart
          plot={plot}
          markType={viewSettings.markType}
          format={format}
          formatOtherSeries={formatOtherSeries}
          formatAccessibleName={formatAccessibleName}
          axisCaptions={NO_CAPTIONS}
          onExportCanvasAvailable={onExportCanvasAvailable}
          exportCaptions={exportCaptions}
          {...chartColors}
        />
      </ChartArea>
    </SpiderLayout>
  );
};
